import { useId } from 'react';

const TAMANOS = {
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-11 w-11',
} as const;

export type SpinnerTamano = keyof typeof TAMANOS;

/**
 * Spinner corporativo AMBEC — anillo minimalista con arco en degradado del azul
 * de marca. Es el ÚNICO spinner de la aplicación (docs/frontend.md).
 * Decorativo (aria-hidden): el contenedor que lo use debe anunciar el estado
 * (role="status" / aria-busy), como hace CargaPagina.
 */
export function Spinner({
  tamano = 'md',
  tono = 'marca',
  className = '',
}: {
  tamano?: SpinnerTamano;
  /** 'marca' sobre fondos claros · 'blanco' sobre botones/fondos azules. */
  tono?: 'marca' | 'blanco';
  className?: string;
}) {
  const id = useId();
  const pista = tono === 'blanco' ? 'rgba(255,255,255,0.25)' : 'rgba(152,208,235,0.30)';
  const inicio = tono === 'blanco' ? 'rgba(255,255,255,0.15)' : '#00428D';
  const fin = tono === 'blanco' ? '#ffffff' : '#98D0EB';

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={`animate-spin [animation-duration:0.8s] [animation-timing-function:cubic-bezier(0.5,0.15,0.5,0.85)] ${TAMANOS[tamano]} ${className}`}
    >
      <defs>
        <linearGradient id={id} x1="12" y1="2.5" x2="7" y2="18" gradientUnits="userSpaceOnUse">
          <stop stopColor={inicio} stopOpacity={tono === 'blanco' ? 0 : 0.15} />
          <stop offset="1" stopColor={fin} />
        </linearGradient>
      </defs>
      {/* Pista completa tenue */}
      <circle cx="12" cy="12" r="9.5" stroke={pista} strokeWidth="2.25" />
      {/* Arco ~270° con degradado firma que se desvanece a transparente (efecto cometa) */}
      <path
        d="M12 2.5 A 9.5 9.5 0 1 1 2.5 12"
        stroke={`url(#${id})`}
        strokeWidth="2.25"
        strokeLinecap="round"
      />
      {/* Punto de cabeza (celeste/blanco) que lidera el giro — le da vida sin recargar */}
      <circle cx="12" cy="2.5" r="1.55" fill={fin} />
    </svg>
  );
}

/**
 * Estado de carga de página completa — úsalo SIEMPRE que una vista esté
 * cargando (rutas, datos iniciales, transiciones de sesión).
 */
export function CargaPagina({ mensaje = 'Cargando' }: { mensaje?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-5"
    >
      <Spinner tamano="lg" />
      <p className="text-[13px] font-normal text-gris">{mensaje}…</p>
    </div>
  );
}
